// src/cockpit4/workspaces/FinanceWorkspace.tsx
// ============================================
// Finance Workspace - 财务中心
//
// 白皮书第六章⑦规格：
//   1. 核心指标：收入/利润/现金流/成本/ROI，按项目/产品线拆分
//   2. 数据来源要注明：每个数字标注"自动统计"还是"人工录入"
//   3. 和项目中心联动：点击数字跳转对应产品
//
// 白皮书铁律一：Obsidian 是唯一大脑，禁止假数据。
//   数据来源：RevenueEngineRuntime（聚合 CRM + SKU + Product Runtime 真实数据）
//   无数据时显示"暂无数据 / 待接入"，不显示假数字。
// ============================================

import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import {
  Wallet,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Percent,
  ArrowUpRight,
  ArrowDownRight,
  RefreshCw,
  AlertCircle,
  Database,
  ShoppingBag,
  Megaphone,
  FileText,
  Lock,
  // P0/P1 标签页导航图标
  LayoutDashboard,
  Gauge,
  ListChecks,
  PiggyBank,
  LineChart,
  PieChart,
  ArrowLeftRight,
  // P2 标签页导航图标
  ClipboardList,
  Tag,
  Banknote,
  ChevronDown,
  HandCoins,
} from "lucide-react";
import RuntimeManager from "../../runtime/RuntimeManager";
import { useUserStore } from "../../stores/userStore";
// P0/P1 新增组件导入
import FinancialStatements from "../components/FinancialStatements";
import FinanceDashboard from "../components/FinanceDashboard";
import TransactionList from "../components/TransactionList";
import BudgetManager from "../components/BudgetManager";
import FinancialForecast from "../components/FinancialForecast";
import CostAnalyzer from "../components/CostAnalyzer";
// V1.5 P0-4：产品级毛利归因表（Profit Engine，哪条业务线真赚钱）
import ProfitBySkuTable from "../components/ProfitBySkuTable";
import AccountPayableReceivable from "../components/AccountPayableReceivable";
// P2-功能8：经营分析与智能建议
import BusinessAnalysisAdvisor from "../components/BusinessAnalysisAdvisor";
// P2-功能9：智能定价助手
import SmartPricingAssistant from "../components/SmartPricingAssistant";
// P2-功能10：投融资支持
import InvestmentFinancingSupport from "../components/InvestmentFinancingSupport";
// v6 变现层：自动收入（AI 自主赚钱飞轮第 4 环）
import AutoIncomePanel from "../components/AutoIncomePanel";
// P0 算账关：客户/成交单对账看板（FDE 补全）
import FinanceReconciliation from "../components/FinanceReconciliation";

// ============================================
// 类型定义（与 RevenueEngine/types.ts 对齐）
// ============================================

interface SourceBreakdown {
  source: 'crm_payment' | 'sku_sales' | 'product_runtime';
  amount: number;
  count: number;
}

interface TodayRevenue {
  date: string;
  todayAmount: number;
  todayCount: number;
  weekAmount: number;
  monthAmount: number;
  yearAmount: number;
  weekOverWeek: number;
  pendingAmount: number;
  avgOrderValue: number;
  dataSource?: 'real' | 'partial' | 'degraded';
  sourceBreakdown?: SourceBreakdown[];
  aggregatedAt?: string;
}

interface ProductRevenue {
  productId: string;
  productName: string;
  todayAmount: number;
  monthAmount: number;
  yearAmount: number;
  customerCount: number;
  share: number;
  growth: number;
}

interface RevenueByProduct {
  date: string;
  products: ProductRevenue[];
}

interface ContentAttribution {
  contentTitle: string;
  platform: string;
  attributedRevenue: number;
  reach: number;
  conversions: number;
  roi: number;
}

interface RevenueByContent {
  date: string;
  contents: ContentAttribution[];
}

// ============================================
// 工具函数
// ============================================

/** 格式化金额（元 → 万元/元）；防御 undefined/NaN/负值，避免渲染崩溃 */
function formatMoney(amount: number): string {
  const num = Number(amount);
  if (!Number.isFinite(num) || num < 0) return "¥ 0";
  if (num >= 10000) {
    return `¥ ${(num / 10000).toFixed(2)} 万`;
  }
  return `¥ ${num.toLocaleString()}`;
}

/** 数据来源标签 */
function DataSourceBadge({ source }: { source?: string }) {
  if (!source || source === 'degraded') {
    return (
      <span className="inline-flex items-center gap-1 text-[9px] font-mono px-1.5 py-0.5 rounded bg-gray-500/10 text-gray-400">
        <AlertCircle size={9} /> 待接入
      </span>
    );
  }
  if (source === 'real') {
    return (
      <span className="inline-flex items-center gap-1 text-[9px] font-mono px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-400">
        <Database size={9} /> 自动统计
      </span>
    );
  }
  if (source === 'partial') {
    return (
      <span className="inline-flex items-center gap-1 text-[9px] font-mono px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-400">
        <Database size={9} /> 部分统计
      </span>
    );
  }
  return null;
}

// ============================================
// 主组件
// ============================================

export default function FinanceWorkspace() {
  const [todayRevenue, setTodayRevenue] = useState<TodayRevenue | null>(null);
  const [byProduct, setByProduct] = useState<RevenueByProduct | null>(null);
  const [byContent, setByContent] = useState<RevenueByContent | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");
  // P0/P1 标签页状态
  const [activeTab, setActiveTab] = useState<string>("dashboard");
  // v6 收敛：低频/重复功能收进"更多▾"下拉（财务仪表盘与数据看板功能重叠）
  const [showMoreTabs, setShowMoreTabs] = useState(false);

  // 权限控制：财务中心仅超级管理员开放，普通员工不开放此权限
  const isSuperAdmin = useUserStore((s) => s.isSuperAdmin());

  /** 从 RevenueEngineRuntime 加载真实财务数据 */
  const loadFinanceData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      // 轮询等待 RuntimeManager 初始化完成（30+ Runtime 全部初始化需要时间）
      let ready = false;
      for (let i = 0; i < 60; i++) {
        if (RuntimeManager.isInitialized()) { ready = true; break; }
        await new Promise((r) => setTimeout(r, 300));
      }
      if (!ready) {
        setError("RuntimeManager 未就绪，请稍后重试");
        return;
      }
      // 检查 RevenueEngine 是否已注册
      if (!RuntimeManager.hasRuntime('revenue_engine' as any)) {
        setError("RevenueEngine 未注册");
        return;
      }
      // 并行查询三个维度
      const [todayResp, productResp, contentResp] = await Promise.all([
        RuntimeManager.query('revenue_engine' as any, { type: 'today_revenue' }),
        RuntimeManager.query('revenue_engine' as any, { type: 'revenue_by_product' }),
        RuntimeManager.query('revenue_engine' as any, { type: 'revenue_by_content' }),
      ]);

      // 双层解包：RevenueEngineRuntime 返回 { success, data, runtime, timestamp }，
      // 又被 RuntimeManager 包裹为 { success, data: <上述对象> }。
      // 因此真实数据在 resp.data.data（若仍非对象则兼容直接返回结构）。
      const unwrap = (resp: any) => RuntimeManager.unwrapResponse(resp);

      const todayData = unwrap(todayResp);
      const productData = unwrap(productResp);
      const contentData = unwrap(contentResp);

      if (todayData) setTodayRevenue(todayData as TodayRevenue);
      if (productData) setByProduct(productData as RevenueByProduct);
      if (contentData) setByContent(contentData as RevenueByContent);
      // 三个查询都无数据时给出明确提示（不阻塞渲染）
      if (!todayData && !productData && !contentData) {
        const firstErr = todayResp?.error || productResp?.error || contentResp?.error;
        if (firstErr) setError(firstErr);
      }
    } catch (err: any) {
      setError(err?.message || "加载财务数据失败");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadFinanceData();
  }, [loadFinanceData]);

  // ============================================
  // 渲染：4 张核心卡片
  // ============================================

  const cards = [
    {
      label: "今日收入",
      value: todayRevenue ? formatMoney(todayRevenue.todayAmount) : "—",
      delta: todayRevenue?.weekOverWeek || 0,
      icon: TrendingUp,
      color: "#10B981",
      subtitle: "Revenue Today",
      source: todayRevenue?.dataSource,
      count: todayRevenue?.todayCount,
    },
    {
      label: "本月收入",
      value: todayRevenue ? formatMoney(todayRevenue.monthAmount) : "—",
      delta: 0,
      icon: DollarSign,
      color: "#06B6D4",
      subtitle: "Month to Date",
      source: todayRevenue?.dataSource,
      count: 0,
    },
    {
      label: "本年收入",
      value: todayRevenue ? formatMoney(todayRevenue.yearAmount) : "—",
      delta: 0,
      icon: Wallet,
      color: "#A855F7",
      subtitle: "Year to Date",
      source: todayRevenue?.dataSource,
      count: 0,
    },
    {
      label: "客单价",
      value: todayRevenue ? formatMoney(todayRevenue.avgOrderValue) : "—",
      delta: 0,
      icon: Percent,
      color: "#F59E0B",
      subtitle: "Avg Order Value",
      source: todayRevenue?.dataSource,
      count: todayRevenue?.todayCount,
    },
  ];

  // 普通员工不开放财务中心权限：显示权限提示，不加载任何财务数据
  if (!isSuperAdmin) {
    return (
      <div className="h-full w-full overflow-hidden p-4">
        <div className="h-full flex flex-col items-center justify-center rounded-xl border border-red-500/20 bg-red-500/[0.02] backdrop-blur-xl">
          <div className="w-16 h-16 rounded-full bg-red-500/15 border border-red-500/30 flex items-center justify-center mb-4">
            <Lock size={28} className="text-red-400" />
          </div>
          <h2 className="text-[16px] font-semibold text-white mb-2">权限受限</h2>
          <p className="text-[12px] font-mono text-gray-400 text-center max-w-sm leading-relaxed">
            财务中心仅对超级管理员开放。
            <br />
            普通员工无权查看财务数据。
          </p>
          <p className="text-[10px] font-mono text-gray-600 mt-4">
            如需访问，请联系超级管理员授权。
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full w-full overflow-hidden p-4">
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        className="h-full flex flex-col rounded-xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-xl overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/[0.06]">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-md bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
              <Wallet size={14} className="text-emerald-400" />
            </div>
            <div>
              <h2 className="text-[15px] font-semibold text-white tracking-tight">
                财务中心
              </h2>
              <p className="text-[10px] font-mono text-gray-500 uppercase tracking-wider">
                收入 · 产品 · 渠道 · 内容归因
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={loadFinanceData}
              disabled={loading}
              className="flex items-center gap-1 text-[10px] font-mono px-2 py-1 rounded border border-white/[0.08] bg-white/[0.03] text-gray-300 hover:bg-white/[0.06] transition disabled:opacity-50"
            >
              <RefreshCw size={10} className={loading ? "animate-spin" : ""} />
              {loading ? "加载中" : "刷新"}
            </button>
          </div>
        </div>

        {/* v6 收敛：核心 5 项平铺 + 低频 6 项收进"更多▾"（财务仪表盘并入数据看板） */}
        <div className="relative z-30 flex items-center gap-1 px-4 py-2 border-b border-white/[0.06] bg-white/[0.01] flex-wrap">
          {/* 核心标签组 */}
          <span className="text-[9px] font-mono text-gray-600 uppercase tracking-wider mr-1">核心</span>
          {([
            { id: "dashboard", label: "数据看板", icon: LayoutDashboard },
            { id: "statements", label: "财务三表", icon: FileText },
            { id: "transactions", label: "收支明细", icon: ListChecks },
            { id: "budget", label: "预算管理", icon: PiggyBank },
            { id: "cost", label: "成本分析", icon: PieChart },
            { id: "autoIncome", label: "自动收入", icon: DollarSign },
          ] as const).map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1 px-2.5 py-1 rounded text-[11px] font-mono transition ${
                  isActive
                    ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"
                    : "text-gray-400 hover:text-gray-200 hover:bg-white/[0.04] border border-transparent"
                }`}
              >
                <Icon size={11} />
                {tab.label}
              </button>
            );
          })}

          {/* 更多▾ 下拉 */}
          <div className="relative">
            <button
              onClick={() => setShowMoreTabs(!showMoreTabs)}
              className={`flex items-center gap-1 px-2.5 py-1 rounded text-[11px] font-mono transition ${
                showMoreTabs
                  ? "bg-white/[0.06] text-white border border-white/[0.12]"
                  : "text-gray-400 hover:text-gray-200 hover:bg-white/[0.04] border border-transparent"
              }`}
            >
              <span>更多</span>
              <ChevronDown size={10} className={showMoreTabs ? "rotate-180 transition-transform" : "transition-transform"} />
            </button>
            {showMoreTabs && (
              <>
                <div className="fixed inset-0 z-30" onClick={() => setShowMoreTabs(false)} />
                <div className="absolute top-full left-0 mt-1 z-40 min-w-[160px] rounded-lg border border-white/[0.1] bg-[#0d1220] p-1 shadow-2xl">
                  {([
                    { id: "financeDashboard", label: "财务仪表盘", icon: Gauge },
                    { id: "forecast", label: "财务预测", icon: LineChart },
                    { id: "reconciliation", label: "算账对账", icon: HandCoins },
                    { id: "ar_ap", label: "应收应付", icon: ArrowLeftRight },
                    { id: "businessAnalysis", label: "经营分析", icon: ClipboardList },
                    { id: "smartPricing", label: "智能定价", icon: Tag },
                    { id: "financing", label: "投融资", icon: Banknote },
                  ] as const).map((tab) => {
                    const Icon = tab.icon;
                    const isActive = activeTab === tab.id;
                    return (
                      <button
                        key={tab.id}
                        onClick={() => {
                          setActiveTab(tab.id);
                          setShowMoreTabs(false);
                        }}
                        className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-[11px] font-mono transition-colors ${
                          isActive
                            ? "text-emerald-300 bg-emerald-500/10"
                            : "text-gray-300 hover:bg-white/[0.05]"
                        }`}
                      >
                        <Icon size={11} className="text-emerald-400" />
                        <span className="flex-1 text-left">{tab.label}</span>
                        {isActive && <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />}
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>

        {/* 内容区：根据 activeTab 渲染对应组件 */}
        {activeTab === "dashboard" && (
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* 错误提示 */}
          {error && (
            <div className="px-4 py-2 rounded-md border border-red-500/20 bg-red-500/[0.03] text-[10px] font-mono text-red-400">
              <AlertCircle size={11} className="inline mr-1" />
              {error}
            </div>
          )}

          {/* 4 张核心卡片 */}
          <div className="grid grid-cols-2 gap-4">
            {cards.map((card, idx) => {
              const Icon = card.icon;
              const isPositive = card.delta > 0;
              const hasData = card.value !== "—";
              return (
                <motion.div
                  key={card.label}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: idx * 0.08, ease: "easeOut" }}
                  className="relative rounded-xl border border-white/[0.06] bg-white/[0.02] p-5 overflow-hidden"
                >
                  <div
                    className="absolute top-0 left-0 right-0 h-px"
                    style={{
                      background: `linear-gradient(90deg, transparent, ${card.color}, transparent)`,
                    }}
                  />
                  <div
                    className="absolute -top-10 -right-10 w-32 h-32 rounded-full pointer-events-none"
                    style={{
                      background: `radial-gradient(circle, ${card.color}25 0%, transparent 70%)`,
                      filter: "blur(20px)",
                    }}
                  />

                  <div className="relative flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div
                        className="w-9 h-9 rounded-lg flex items-center justify-center"
                        style={{
                          background: `${card.color}15`,
                          border: `1px solid ${card.color}30`,
                        }}
                      >
                        <Icon size={16} style={{ color: card.color }} />
                      </div>
                      <div>
                        <h3 className="text-[13px] font-medium text-white">
                          {card.label}
                        </h3>
                        <p className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">
                          {card.subtitle}
                        </p>
                      </div>
                    </div>
                    <DataSourceBadge source={card.source} />
                  </div>

                  <div className="relative mb-2">
                    {hasData ? (
                      <span
                        className="text-[28px] font-mono font-bold tracking-tight"
                        style={{ color: card.color }}
                      >
                        {card.value}
                      </span>
                    ) : (
                      <span className="text-[16px] font-mono text-gray-600">
                        暂无数据
                      </span>
                    )}
                  </div>

                  <div className="relative flex items-center justify-between text-[10px] font-mono">
                    {card.count !== undefined && card.count > 0 ? (
                      <span className="text-gray-400">{card.count} 笔成交</span>
                    ) : (
                      <span />
                    )}
                    {card.delta !== 0 && hasData && (
                      <span
                        className={`flex items-center gap-0.5 ${
                          isPositive ? "text-emerald-400" : "text-red-400"
                        }`}
                      >
                        {isPositive ? <ArrowUpRight size={10} /> : <ArrowDownRight size={10} />}
                        {Math.abs(card.delta) * 100}%
                      </span>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* 收入来源分解（白皮书：数据来源要注明） */}
          {todayRevenue?.sourceBreakdown && todayRevenue.sourceBreakdown.length > 0 && (
            <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4">
              <div className="flex items-center gap-2 mb-3">
                <Database size={12} className="text-blue-400" />
                <h3 className="text-[12px] font-medium text-white">收入来源分解</h3>
                <span className="text-[9px] font-mono text-gray-500">（数据来源标注）</span>
              </div>
              <div className="space-y-2">
                {todayRevenue.sourceBreakdown.map((src) => {
                  const labelMap: Record<string, { label: string; icon: typeof Database; color: string }> = {
                    crm_payment: { label: 'CRM 成交', icon: ShoppingBag, color: '#10B981' },
                    sku_sales: { label: 'SKU 销售', icon: DollarSign, color: '#06B6D4' },
                    product_runtime: { label: '产品估算', icon: Megaphone, color: '#A855F7' },
                  };
                  const cfg = labelMap[src.source] || { label: src.source, icon: Database, color: '#6B7280' };
                  const Icon = cfg.icon;
                  return (
                    <div key={src.source} className="flex items-center justify-between text-[11px] font-mono">
                      <div className="flex items-center gap-2">
                        <Icon size={11} style={{ color: cfg.color }} />
                        <span className="text-gray-300">{cfg.label}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-gray-400">{src.count} 笔</span>
                        <span className="text-white" style={{ color: cfg.color }}>
                          {formatMoney(src.amount)}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* V1.5 P0-4：产品毛利归因（Profit Engine） */}
          <ProfitBySkuTable />

          {/* 按产品维度拆分（白皮书：按项目/产品线拆分 + 与项目中心联动） */}
          <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4">
            <div className="flex items-center gap-2 mb-3">
              <ShoppingBag size={12} className="text-purple-400" />
              <h3 className="text-[12px] font-medium text-white">按产品线拆分</h3>
              <span className="text-[9px] font-mono text-gray-500">（点击跳转项目中心）</span>
            </div>
            {byProduct?.products && byProduct.products.length > 0 ? (
              <div className="space-y-2">
                {byProduct.products.map((p) => (
                  <div
                    key={p.productId}
                    className="flex items-center justify-between p-2 rounded border border-white/[0.04] bg-white/[0.01] hover:bg-white/[0.03] cursor-pointer transition"
                    onClick={() => {
                      // 白皮书：与项目中心联动
                      window.dispatchEvent(new CustomEvent('xeaos:navigate', { detail: { workspace: 'projects', productId: p.productId } }));
                    }}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-[11px] text-gray-200">{p.productName}</span>
                      <span className="text-[9px] font-mono text-gray-500">
                        {p.customerCount} 客户 · 占比 {(p.share * 100).toFixed(0)}%
                      </span>
                    </div>
                    <div className="flex items-center gap-3 text-[11px] font-mono">
                      <span className="text-gray-400">月 {formatMoney(p.monthAmount)}</span>
                      <span className="text-white">{formatMoney(p.yearAmount)}</span>
                      <span
                        className={`flex items-center gap-0.5 ${
                          p.growth > 0 ? "text-emerald-400" : "text-red-400"
                        }`}
                      >
                        {p.growth > 0 ? <ArrowUpRight size={10} /> : <ArrowDownRight size={10} />}
                        {Math.abs(p.growth * 100).toFixed(0)}%
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6 text-[11px] font-mono text-gray-600">
                <AlertCircle size={16} className="inline mr-1.5 mb-0.5" />
                暂无产品收入数据 · 待 RevenueEngine 接入
              </div>
            )}
          </div>

          {/* 内容归因（白皮书：营销中心联动 - 哪类内容带来收入） */}
          <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4">
            <div className="flex items-center gap-2 mb-3">
              <FileText size={12} className="text-orange-400" />
              <h3 className="text-[12px] font-medium text-white">内容归因收入</h3>
              <span className="text-[9px] font-mono text-gray-500">（哪类内容带来收入）</span>
            </div>
            {byContent?.contents && byContent.contents.length > 0 ? (
              <div className="space-y-2">
                {byContent.contents.map((c, i) => (
                  <div key={i} className="flex items-center justify-between p-2 rounded border border-white/[0.04] bg-white/[0.01]">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-300 flex-shrink-0">
                        {c.platform}
                      </span>
                      <span className="text-[11px] text-gray-300 truncate">{c.contentTitle}</span>
                    </div>
                    <div className="flex items-center gap-3 text-[11px] font-mono flex-shrink-0">
                      <span className="text-gray-400">触达 {c.reach}</span>
                      <span className="text-gray-400">转化 {c.conversions}</span>
                      <span className="text-emerald-400">{formatMoney(c.attributedRevenue)}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6 text-[11px] font-mono text-gray-600">
                <AlertCircle size={16} className="inline mr-1.5 mb-0.5" />
                暂无内容归因数据 · 待 CRM 内容来源记录接入
              </div>
            )}
          </div>

          {/* 数据来源说明（白皮书：数据来源要注明） */}
          <div className="px-4 py-2 rounded-md border border-blue-500/15 bg-blue-500/[0.02] text-[10px] font-mono text-blue-300/70">
            <Database size={11} className="inline mr-1" />
            数据来源：RevenueEngineRuntime 聚合 CRM Pipeline + SKU 销售记录 + Product Runtime 预期收入
            {todayRevenue?.aggregatedAt && (
              <span className="ml-2 text-gray-500">
                · 最后聚合：{new Date(todayRevenue.aggregatedAt).toLocaleTimeString('zh-CN')}
              </span>
            )}
          </div>
        </div>
        )}

        {/* 其他标签页渲染 */}
        {activeTab !== "dashboard" && (
          <div className="flex-1 overflow-hidden">
            {activeTab === "statements" && <FinancialStatements />}
            {activeTab === "financeDashboard" && <FinanceDashboard />}
            {activeTab === "transactions" && <TransactionList />}
            {activeTab === "budget" && <BudgetManager />}
            {activeTab === "forecast" && <FinancialForecast />}
            {activeTab === "cost" && <CostAnalyzer />}
            {activeTab === "autoIncome" && <AutoIncomePanel />}
            {activeTab === "ar_ap" && <AccountPayableReceivable />}
            {activeTab === "reconciliation" && <FinanceReconciliation />}

            {/* P2 组件 */}
            {activeTab === "businessAnalysis" && <BusinessAnalysisAdvisor />}
            {activeTab === "smartPricing" && <SmartPricingAssistant />}
            {activeTab === "financing" && <InvestmentFinancingSupport />}
          </div>
        )}
      </motion.div>
    </div>
  );
}
